// client/src/components/pages/LexiStar/StatusBoard/KanbanBoard.tsx (converted from .jsx, ST-00206)
//
// Kanban grouped by operational context (FR-STAT-003): one column per context
// group in load order, one card per truck. Also the phone layout of the board
// (LS-09 §9 "status board (cards)").
//
// ST-00206: a card of a DISPATCHED trip the driver has not accepted shows
// "reminded ×N" (AckRemindersTag) after its status pill. ST-00205: and "waiting for first
// report" (AwaitingReportTag) while the truck has not reported since the dispatch.

import {
  CONTEXT_GROUPS as CONTEXT_GROUPS_JS,
  contextGroupOf as contextGroupOfJs,
} from './statusLanguage';
import { ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import { statusKeyOfRow } from './controlRoom';
import {
  StatusPill,
  DataAge,
  RunChip,
  BurnIndex,
  AckRemindersTag,
  AwaitingReportTag,
  type RunSummary,
} from './atoms';
import {
  DistanceCell,
  FuelUsedCell,
  EtaCell,
  rowRegistration,
  rowDriver,
  rowUpdatedAt,
} from './columns';
import type { StatusBoardRow } from '../../../../types/lexistar-status-board';

/** A Kanban column: the contexts it gathers and its status key (statusLanguage CONTEXT_GROUPS). */
interface ContextGroup {
  id: string;
  label: string;
  contexts: string[];
  tone: AssetStatusKey;
}
const CONTEXT_GROUPS = CONTEXT_GROUPS_JS as ContextGroup[];
const contextGroupOf = contextGroupOfJs as (context: string | null | undefined) => ContextGroup;

export interface KanbanBoardProps {
  rows: StatusBoardRow[];
  onOpenTrip: (row: StatusBoardRow) => void;
  onOpenRun: (run: RunSummary) => void;
  now?: number;
}

export default function KanbanBoard({ rows, onOpenTrip, onOpenRun, now }: KanbanBoardProps) {
  const byGroup = new Map<string, StatusBoardRow[]>(CONTEXT_GROUPS.map((g) => [g.id, []]));
  rows.forEach((r) => byGroup.get(contextGroupOf(r.status?.context).id)?.push(r));
  const inGroup = (g: ContextGroup): StatusBoardRow[] => byGroup.get(g.id) ?? [];
  const groups = CONTEXT_GROUPS.filter((g) => inGroup(g).length > 0);

  return (
    <div
      className="flex gap-4 overflow-x-auto pb-2"
      role="list"
      aria-label="Status board by operational context"
    >
      {groups.map((g) => (
        <section
          key={g.id}
          role="listitem"
          aria-label={`${g.label} (${inGroup(g).length})`}
          className="flex-shrink-0 w-72 bg-slate-50 border border-slate-200 rounded-xl"
        >
          <header className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${ASSET_STATUS_CLASSES[g.tone].dot}`}
                aria-hidden="true"
              />
              {g.label}
            </span>
            <span className="text-xs font-semibold text-slate-500 tabular-nums">
              {inGroup(g).length}
            </span>
          </header>
          <ul className="p-2 space-y-2">
            {inGroup(g).map((r) => (
              <li key={r.asset_key}>
                <article
                  className="p-3 bg-surface border border-slate-200 rounded-lg shadow-sm hover:border-primary-300 transition-colors"
                  aria-label={`${rowRegistration(r)} card`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {r.trip ? (
                      <button
                        type="button"
                        onClick={() => onOpenTrip(r)}
                        className="text-sm font-mono font-semibold text-slate-900 hover:text-primary-600 hover:underline"
                        title="Open trip detail"
                      >
                        {rowRegistration(r)}
                      </button>
                    ) : (
                      <span className="text-sm font-mono font-semibold text-slate-900">
                        {rowRegistration(r)}
                      </span>
                    )}
                    <DataAge ts={rowUpdatedAt(r)} now={now} />
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">{rowDriver(r) || 'No driver'}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    <StatusPill status={r.status} statusKey={statusKeyOfRow(r, now ?? Date.now())} display={r.display} />
                    <AckRemindersTag trip={r.trip} />
                    <AwaitingReportTag trip={r.trip} />
                  </div>
                  {r.trip && (
                    <div className="mt-2 space-y-1 text-xs text-slate-600">
                      <div className="truncate">
                        {r.trip.from_name} → {r.trip.to_name}
                      </div>
                      <DistanceCell trip={r.trip} />
                      <div className="flex items-center justify-between gap-2">
                        <FuelUsedCell fuel={r.fuel} />
                        {r.fuel && (
                          <BurnIndex value={r.fuel.burn_index} pctDistance={r.trip.pct_distance} />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span>
                          ETA <EtaCell trip={r.trip} />
                        </span>
                        <RunChip run={r.run} onOpen={onOpenRun} />
                      </div>
                    </div>
                  )}
                </article>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
